import { AuthenticationUser, useRefreshTokenMutation } from '@codezo/graphql';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { formatLoginUser } from '../../lib/format-auth-user';
import { bindGuestSessionUser, getGuestSession } from '../../lib/guest-session';
import { GuestAuthChallenge, GuestNotice } from './GuestAuthChallenge';
import { GuestCard } from './GuestCard';

type SessionState = 'restoring' | 'ready' | 'challenge';

/**
 * Holds a guest session together for the routes behind a guest link.
 *
 * Guest routes are deliberately NOT behind PrivateRoute: a guest has no account
 * to sign back into, so instead of bouncing them to a login page this restores
 * the session from the refresh cookie and — if that fails — asks for a new code
 * in place, on the same URL.
 */
export function GuestSessionBoundary({ children }: { children: ReactNode }) {
	const { user, token, setUser, setAccessToken } = useAuth();
	const [refreshToken] = useRefreshTokenMutation();
	const attempted = useRef(false);

	const hasSession = !!user && !!token;
	const [state, setState] = useState<SessionState>(hasSession ? 'ready' : 'restoring');

	// Access tokens live in memory, so a reload arrives here empty-handed. The
	// refresh cookie is the normal way back in; only a failed refresh means the
	// guest genuinely has to re-verify.
	useEffect(() => {
		if (hasSession) {
			setState('ready');
			return;
		}
		if (attempted.current) return;
		attempted.current = true;

		(async () => {
			try {
				const { data } = await refreshToken();
				const refreshed = data?.refreshToken;
				if (!refreshed?.user) {
					setState('challenge');
					return;
				}
				setUser(formatLoginUser(refreshed.user as AuthenticationUser));
				setAccessToken(refreshed.token);
				setState('ready');
			} catch {
				setState('challenge');
			}
		})();
	}, [hasSession, refreshToken, setUser, setAccessToken]);

	// Remember which user this guest tab belongs to, so a later normal sign-in
	// in the same tab isn't mistaken for the guest.
	useEffect(() => {
		if (user?.id) bindGuestSessionUser(user.id);
	}, [user?.id]);

	if (state === 'restoring') {
		return (
			<div className="flex h-full items-center justify-center gap-2 text-sm text-text-3">
				<Loader2 size={15} className="animate-spin" />
				Restoring your session…
			</div>
		);
	}

	if (state === 'challenge') {
		const session = getGuestSession();
		const linkToken = session?.linkToken ?? null;
		/* A preview has no way back: its token was single-use, and there is no
		   mailbox behind it to send a code to. Say where a new one comes from
		   instead of offering a challenge that cannot succeed. */
		if (session?.preview) {
			return (
				<GuestCard>
					<GuestNotice
						title="This preview session has ended"
						detail="Press “Preview as guest” on the quote in the admin portal to open a new one."
					/>
				</GuestCard>
			);
		}
		return (
			<GuestCard>
				{linkToken ? (
					<GuestAuthChallenge
						linkToken={linkToken}
						expired
						onAuthenticated={() => setState('ready')}
					/>
				) : (
					<GuestNotice
						title="Your session has ended"
						detail="Open the link from your email again to pick up where you left off."
					/>
				)}
			</GuestCard>
		);
	}

	return <>{children}</>;
}
